/**
 * Monsterplan exporteren: de getekende (deel)locaties en de boorpunten naar een bestand dat
 * een ander programma kan lezen.
 *
 *   - GeoJSON — QGIS, ArcGIS en webkaarten; in RD (met crs EPSG:28992, dat QGIS leest) of WGS84
 *   - KML     — Google Earth en de meeste GPS-apps; altijd WGS84 (de KML-norm), RD X/Y als gegevens
 *   - DXF     — AutoCAD en de landmeter, in RD-meters (EPSG:28992), één laag per soort
 *   - CSV     — uitzetlijst voor de GPS, RD X/Y met puntkomma en decimale komma (Excel-NL)
 *
 * Alles werkt op één verzameling: `{ vlakken, punten }`, waarin een vlak een deellocatie is
 * (ring in [lng, lat]) en een punt een gepland, nog niet opgeslagen (concept) of al geboord punt.
 * Met `sublocationId` alleen die deellocatie en de punten die erin liggen.
 * De omrekening naar RD gebeurt hier, zodat de knop in het paneel alleen nog hoeft te kiezen.
 */
import { convertFromWgs84 } from '@mv/models';
import { inSublocation } from './sublocation-geometry.js';

/** `systems`: welke stelsels het formaat kan; het eerste is de standaard. */
export const EXPORT_FORMATS = [
  { id: 'geojson', label: 'GeoJSON', extension: 'geojson', mime: 'application/geo+json', hint: 'QGIS, ArcGIS', systems: ['RD', 'WGS84'] },
  { id: 'kml', label: 'KML', extension: 'kml', mime: 'application/vnd.google-earth.kml+xml', hint: 'Google Earth, GPS-apps · RD X/Y als gegevens', systems: ['WGS84'] },
  { id: 'dxf', label: 'DXF', extension: 'dxf', mime: 'application/dxf', hint: 'AutoCAD, landmeter', systems: ['RD'] },
  { id: 'csv', label: 'CSV', extension: 'csv', mime: 'text/csv', hint: 'uitzetlijst voor de GPS · RD én WGS84', systems: ['RD'] },
];

const STATUS_LABELS = { gepland: 'gepland', concept: 'concept (niet opgeslagen)', geboord: 'geboord' };
const KIND_LABELS = { boring: 'boring', peilbuis: 'peilbuis', proefgat: 'proefgat', sleuf: 'sleuf', overig: 'overig' };

/**
 * Verzamel wat er op de vooronderzoekkaart staat.
 * @param {object} bron
 * @param {Array} bron.sublocations  { name, geometry: [[lng, lat]…], strategyCode, areaM2 }
 * @param {Array} bron.plannedPoints { name, kind, x: lng, y: lat, plannedDepthCm, note }
 * @param {Array} [bron.concept]     { name, kind, lng, lat, depthCm } — nog niet opgeslagen
 * @param {Array} [bron.borings]     { name, lat, lng } — al geboord
 */
export function collectMonsterplan({ sublocations = [], plannedPoints = [], concept = [], borings = [], sublocationId = null }) {
  const gebied = sublocationId ? sublocations.find((s) => s.id === sublocationId) : null;
  if (sublocationId && !gebied) throw new Error('Die deellocatie bestaat niet meer.');
  const binnen = (lng, lat) => !gebied || inSublocation(lng, lat, gebied);
  const vlakken = (gebied ? [gebied] : sublocations)
    .filter((s) => (s.geometry || []).length >= 3)
    .map((s) => ({ name: s.name, ring: s.geometry, strategy: s.strategyCode || '', areaM2: s.areaM2 ?? null }));
  const punten = [
    ...plannedPoints.filter((p) => p.x != null && p.y != null).map((p) => ({
      name: p.name, kind: p.kind || 'boring', lng: p.x, lat: p.y, depthCm: p.plannedDepthCm ?? null,
      status: 'gepland', note: p.note || '',
    })),
    ...concept.map((p) => ({
      name: p.name, kind: p.kind || 'boring', lng: p.lng, lat: p.lat, depthCm: p.depthCm ?? null, status: 'concept', note: '',
    })),
    ...borings.filter((b) => b.lat != null && b.lng != null).map((b) => ({
      name: b.name, kind: 'boring', lng: b.lng, lat: b.lat, depthCm: null, status: 'geboord', note: '',
    })),
  ].filter((p) => binnen(p.lng, p.lat));
  return { vlakken, punten, gebied: gebied?.name ?? null };
}

/** Gesloten ring: het eerste punt ook als laatste. */
const sluit = (ring) => {
  const [a, b] = [ring[0], ring[ring.length - 1]];
  return a[0] === b[0] && a[1] === b[1] ? ring : [...ring, a];
};

const rd = ([lng, lat]) => {
  const p = convertFromWgs84(lng, lat, 'RD');
  if (!p) throw new Error('Een punt ligt buiten het bereik van RD; kies GeoJSON of KML.');
  return p;
};

const mm = (n) => Math.round(n * 1000) / 1000;

/** RD X/Y van een punt, op de millimeter. */
const rdXY = (p) => { const { x, y } = rd([p.lng, p.lat]); return { rdX: mm(x), rdY: mm(y) }; };

export function toGeoJson({ vlakken, punten }, system = 'RD') {
  const c = system === 'RD' ? (ll) => { const { x, y } = rd(ll); return [mm(x), mm(y)]; } : (ll) => ll;
  return JSON.stringify({
    type: 'FeatureCollection',
    ...(system === 'RD' ? { crs: { type: 'name', properties: { name: 'urn:ogc:def:crs:EPSG::28992' } } } : {}),
    features: [
      ...vlakken.map((v) => ({
        type: 'Feature',
        properties: { type: 'deellocatie', name: v.name, strategy: v.strategy || null, areaM2: v.areaM2 },
        geometry: { type: 'Polygon', coordinates: [sluit(v.ring).map(c)] },
      })),
      ...punten.map((p) => ({
        type: 'Feature',
        properties: { type: p.kind, name: p.name, status: p.status, depthCm: p.depthCm, note: p.note || null, ...rdXY(p) },
        geometry: { type: 'Point', coordinates: c([p.lng, p.lat]) },
      })),
    ],
  }, null, 2);
}

const xml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const diepte = (cm) => (cm == null ? '' : `${String(cm / 100).replace('.', ',')} m -mv`);

export function toKml({ vlakken, punten }, naam = 'Monsterplan') {
  const stijlen = {
    vlak: '<Style id="vlak"><LineStyle><color>ff0c41c2</color><width>2</width></LineStyle><PolyStyle><color>330c41c2</color></PolyStyle></Style>',
    boring: '<Style id="boring"><IconStyle><color>ff4aa316</color><Icon><href>http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png</href></Icon></IconStyle></Style>',
    peilbuis: '<Style id="peilbuis"><IconStyle><color>ffeb6325</color><Icon><href>http://maps.google.com/mapfiles/kml/shapes/placemark_square.png</href></Icon></IconStyle></Style>',
    geboord: '<Style id="geboord"><IconStyle><color>ff72665b</color><Icon><href>http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png</href></Icon></IconStyle></Style>',
  };
  const coord = ([lng, lat]) => `${lng},${lat},0`;
  const vlakXml = vlakken.map((v) => `
    <Placemark><name>${xml(v.name)}</name>
      <description>${xml([v.strategy && `Strategie ${v.strategy}`, v.areaM2 && `${Math.round(v.areaM2)} m²`].filter(Boolean).join(' · '))}</description>
      <styleUrl>#vlak</styleUrl>
      <Polygon><outerBoundaryIs><LinearRing><coordinates>${sluit(v.ring).map(coord).join(' ')}</coordinates></LinearRing></outerBoundaryIs></Polygon>
    </Placemark>`).join('');
  const puntXml = punten.map((p) => { const { rdX, rdY } = rdXY(p); return `
    <Placemark><name>${xml(p.name)}</name>
      <description>${xml([KIND_LABELS[p.kind] || p.kind, STATUS_LABELS[p.status], diepte(p.depthCm), `RD ${rdX.toFixed(2)} / ${rdY.toFixed(2)}`, p.note].filter(Boolean).join(' · '))}</description>
      <ExtendedData><Data name="rdX"><value>${rdX.toFixed(3)}</value></Data><Data name="rdY"><value>${rdY.toFixed(3)}</value></Data></ExtendedData>
      <styleUrl>#${p.status === 'geboord' ? 'geboord' : p.kind === 'peilbuis' ? 'peilbuis' : 'boring'}</styleUrl>
      <Point><coordinates>${coord([p.lng, p.lat])}</coordinates></Point>
    </Placemark>`; }).join('');
  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${xml(naam)}</name>
  ${Object.values(stijlen).join('\n  ')}
  <Folder><name>Deellocaties</name>${vlakXml}
  </Folder>
  <Folder><name>Boorpunten</name>${puntXml}
  </Folder>
</Document></kml>
`;
}

/**
 * DXF R12 (AC1009): het oudste formaat dat elk CAD-pakket nog leest, zonder handles of
 * objecttabellen. Lagen: DEELLOCATIE, BORING_GEPLAND, PEILBUIS_GEPLAND, BORING_GEBOORD,
 * PUNTNAAM. Een punt is een cirkel van 0,25 m met de naam ernaast.
 */
export function toDxf({ vlakken, punten }) {
  const regels = [];
  const zet = (...paren) => { for (let i = 0; i < paren.length; i += 2) regels.push(String(paren[i]), String(paren[i + 1])); };
  const getal = (n) => n.toFixed(3);
  const laagVan = (p) => {
    if (p.status === 'geboord') return 'BORING_GEBOORD';
    return `${p.kind === 'peilbuis' ? 'PEILBUIS' : 'BORING'}_${p.status === 'concept' ? 'CONCEPT' : 'GEPLAND'}`;
  };
  const lagen = { DEELLOCATIE: 30, BORING_GEPLAND: 3, BORING_CONCEPT: 3, PEILBUIS_GEPLAND: 5, PEILBUIS_CONCEPT: 5, BORING_GEBOORD: 8, PUNTNAAM: 7 };

  zet(0, 'SECTION', 2, 'HEADER', 9, '$ACADVER', 1, 'AC1009', 9, '$INSUNITS', 70, 6, 0, 'ENDSEC');
  zet(0, 'SECTION', 2, 'TABLES', 0, 'TABLE', 2, 'LAYER', 70, Object.keys(lagen).length);
  for (const [naam, kleur] of Object.entries(lagen)) zet(0, 'LAYER', 2, naam, 70, 0, 62, kleur, 6, 'CONTINUOUS');
  zet(0, 'ENDTAB', 0, 'ENDSEC', 0, 'SECTION', 2, 'ENTITIES');

  for (const v of vlakken) {
    zet(0, 'POLYLINE', 8, 'DEELLOCATIE', 66, 1, 70, 1);
    for (const p of v.ring) { const { x, y } = rd(p); zet(0, 'VERTEX', 8, 'DEELLOCATIE', 10, getal(x), 20, getal(y)); }
    zet(0, 'SEQEND', 8, 'DEELLOCATIE');
    const m = v.ring.reduce((a, p) => [a[0] + p[0] / v.ring.length, a[1] + p[1] / v.ring.length], [0, 0]);
    const { x, y } = rd(m);
    zet(0, 'TEXT', 8, 'DEELLOCATIE', 10, getal(x), 20, getal(y), 40, 1.5, 1, [v.name, v.strategy].filter(Boolean).join(' '));
  }
  for (const p of punten) {
    const { x, y } = rd([p.lng, p.lat]);
    zet(0, 'CIRCLE', 8, laagVan(p), 10, getal(x), 20, getal(y), 40, 0.25);
    zet(0, 'TEXT', 8, 'PUNTNAAM', 10, getal(x + 0.4), 20, getal(y + 0.2), 40, 0.6, 1, p.name);
  }
  zet(0, 'ENDSEC', 0, 'EOF');
  return regels.join('\r\n') + '\r\n';
}

/** Uitzetlijst: één regel per punt, RD in meters met twee decimalen. Vlakken staan er niet in. */
export function toCsv({ punten }) {
  const veld = (s) => (/[;"\n]/.test(String(s)) ? `"${String(s).replace(/"/g, '""')}"` : String(s ?? ''));
  const komma = (n, d) => n.toFixed(d).replace('.', ',');
  const kop = ['Naam', 'Soort', 'Status', 'X (RD)', 'Y (RD)', 'Diepte (m -mv)', 'Breedtegraad', 'Lengtegraad', 'Opmerking'];
  const rijen = punten.map((p) => {
    const { x, y } = rd([p.lng, p.lat]);
    return [p.name, KIND_LABELS[p.kind] || p.kind, STATUS_LABELS[p.status], komma(x, 2), komma(y, 2),
      p.depthCm == null ? '' : komma(p.depthCm / 100, 2), komma(p.lat, 7), komma(p.lng, 7), p.note].map(veld).join(';');
  });
  // BOM, zodat Excel de ² en ë goed leest.
  return '﻿' + [kop.join(';'), ...rijen].join('\r\n') + '\r\n';
}

/** Het bestand voor één formaat: { text, mime, extension }. `system` telt alleen voor GeoJSON. */
export function exportMonsterplan(format, verzameling, { naam = 'Monsterplan', system = 'RD' } = {}) {
  const f = EXPORT_FORMATS.find((x) => x.id === format);
  if (!f) throw new Error(`Onbekend formaat: ${format}`);
  const text = format === 'geojson' ? toGeoJson(verzameling, f.systems.includes(system) ? system : f.systems[0])
    : format === 'kml' ? toKml(verzameling, naam)
      : format === 'dxf' ? toDxf(verzameling)
        : toCsv(verzameling);
  return { text, mime: f.mime, extension: f.extension };
}
